import { JSX } from 'react';
import DOMPurify from 'dompurify';
import { frontUrl } from "../utils/url-manager";
import { TemplateEditor } from "./components/TemplateEditor";
import { TemplateVariable } from "./components/template-variables";

// The three table variables were previously all documented as "this will
// inject the Invoice Status", and {{items_table}} was substituted but never
// documented at all.
export const INVOICE_VARIABLES: TemplateVariable[] = [
    { token: '{{invoice_id}}',               description: 'The invoice reference.' },
    { token: '{{invoice_status}}',           description: 'The status badge for the invoice, such as Due or Paid.' },
    { token: '{{invoice_data_table}}',       description: 'A table of invoice details: reference, issue date, due date and currency.' },
    { token: '{{items_table}}',              description: 'A table of the line items being billed, with seats and prices.' },
    { token: '{{totals_data_table}}',        description: 'The totals block: subtotal, discount, tax and the amount due.' },
    { token: '{{customer_billing_address}}', description: "The customer's billing address, as an <address> block." },
    { token: '{{invoice_login_url}}',        description: 'The account page where customers view their invoices.' },
    { token: '{{login_url}}',                description: 'The customer login page.' },
    { token: '{{verify_url}}',               description: "A link to verify the recipient's email address." },
    { token: '{{support_email}}',            description: 'The NokNok support address.' },
    { token: '{{current_year}}',             description: 'The current year, for copyright lines.' },
    // HQ-05. In the stored invoice template and substituted by `InvoicePDFGenerator`, which
    // inlines the logo as a base64 data URI so the PDF carries no remote image. It was
    // missing from this list, so the preview showed a literal `{{logo_source}}`.
    { token: '{{logo_source}}',              description: 'The NokNok logo, inlined into the PDF as a data URI. Use it as an <img> src.' },
];

export function buildSamples(): Record<string, string> {
    const account: string = frontUrl + 'account/';

    return {
        '{{support_email}}': 'support@noknok.ai',
        '{{current_year}}': new Date().getFullYear().toString(),
        '{{login_url}}': account,
        '{{verify_url}}': account,
        '{{invoice_login_url}}': account,
        '{{invoice_id}}': 's3hfgIduFhgu',
        // A 1x1 transparent PNG, so the preview shows the <img> in place without shipping a
        // copy of the real logo into the bundle.
        '{{logo_source}}': 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
        '{{invoice_status}}': '<div><span class="badge">Due</span></div>',
        '{{items_table}}': `
                <table class="items">
                    <thead>
                      <tr>
                        <th style="width:50%;">Subscription Tier</th>
                        <th style="width:15%;">Seats</th>
                        <th style="width:15%;">Price (p/m)</th>
                        <th style="width:20%;" class="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr class="avoid-break">
                        <td>
                          BusinessPlus
                          <div class="desc">15 Domains, 25 active Contacts</div>
                        </td>
                        <td class="nowrap">20</td>
                        <td class="nowrap">£20.00</td>
                        <td class="text-right nowrap">£4,00.00</td>
                      </tr>
                    </tbody>
                </table>
            `,
        '{{customer_billing_address}}': `
                <address style="width:100%;">
                    Joe Bloggs</br>
                    123 Street </br>
                    In A City </br>
                    Some County </br>
                    AA1 1AA</br>
                </address>
            `,
        '{{invoice_data_table}}': `
                <table cellpadding="0" cellspacing="0" style="margin-left:auto; font-size:12px;">
                    <tbody><tr><td>Invoice #</td><td style="padding-left:10px;"><strong>s3hfgIduFhgu</strong></td></tr>
                        <tr><td>Date</td><td style="padding-left:10px;">10 Oct 2025</td></tr>
                        <tr><td>Due</td><td style="padding-left:10px;">24 Oct 2025</td></tr>
                        <tr><td>Currency</td><td style="padding-left:10px;">GBP (£)</td></tr>
                    </tbody>
                </table>
            `,
        '{{totals_data_table}}': `
                <table style="width:100%; border-collapse:collapse;">
                    <tbody><tr class="line"><td>Subtotal</td><td class="text-right">£4,00.00</td></tr>
                      <tr class="line"><td>Discount (50%)</td><td class="text-right">-£2,00.00</td></tr>
                      <tr class="line"><td>Tax (VAT 20%)</td><td class="text-right">£40.00</td></tr>
                      <tr class="grand"><td><strong>Total Due</strong></td><td class="text-right"><strong>£2,40.00</strong></td></tr>
                    </tbody>
                </table>
            `,
    };
}

/** The invoice is a whole document, so its structural tags are kept. */
function sanitizeInvoiceHtml(value: string): string {
    return DOMPurify.sanitize(value, {
        WHOLE_DOCUMENT: true,
        ADD_TAGS: ['head', 'meta', 'body', 'title', 'DOCTYPE'],
    });
}

export const SystemDefaultHTMLTemplateCodeEditor: () => JSX.Element = (): JSX.Element => (
    <TemplateEditor
        intro={ 'A complete HTML document for the customer invoice, including its own <style> block. '
            + 'Script tags are removed as you type.' }
        variables={ INVOICE_VARIABLES }
        buildSamples={ buildSamples }
        sanitize={ sanitizeInvoiceHtml }
        height="70vh"
    />
);
